import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import s from './About.module.css';

gsap.registerPlugin(ScrollTrigger);

export default function About() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const statsRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
  const ctx = gsap.context(() => {
      if (!titleRef.current) return;

      const revealLines = gsap.utils.toArray(`[class*="revealLine"]`);
      const maskSpans = gsap.utils.toArray(`[class*="mask"] span`);
      if (revealLines.length === 0 || maskSpans.length === 0) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: titleRef.current,
          start: "top 90%",
          toggleActions: "play none none reverse",
        }
      });

      // Étape 1 : La ligne s'étire horizontalement
      tl.to(revealLines, {
        scaleX: 1,
        duration: 0.8,
        ease: "expo.inOut"
      })
      
      // Étape 2 : Le texte descend de la ligne (Reveal)
      .to(maskSpans, {
        y: "0%",
        duration: 1,
        stagger: 0.2,
        ease: "power4.out"
      }, "-=0.4") 

      // Étape 3 : La ligne s'efface doucement
      .to(revealLines, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.in"
      }, "+=0.2");

    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      
      // Animation des cartes d'info
      if (cardsRef.current && cardsRef.current.children.length > 0) {
        gsap.from(cardsRef.current.children, {
          y: 20,
          opacity: 0,
          stagger: 0.2,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: cardsRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          }
        });
      }

      // Compteur des stats (Correction du sélecteur pour CSS Modules)
      if (statsRef.current) {
        const stats = statsRef.current.querySelectorAll(`[class*="statNumber"]`);
        stats.forEach(stat => {
          const targetValue = parseInt(stat.getAttribute('data-value'));
          gsap.to(stat, {
            innerText: targetValue,
            duration: 2,
            snap: { innerText: 1 },
            scrollTrigger: {
              trigger: stat,
              start: "top 90%",
            }
          });
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="about" className={s.aboutSection}>
      <div className={s.container}>
        <div className={s.content}>
        <div className={s.titleWrapper}>
          {/* La ligne qui va s'animer horizontalement */}
          <div className={s.revealLine}></div>
          
          <h4 className={s.mainTitle} ref={titleRef}>
            <div className={s.mask}>
              <span>Optimisez le suivi de vos bétails</span>
            </div>
            <div className={s.mask}>
              <span className={s.italic}>grâce à la puissance de l'IA</span>
            </div>
          </h4>
        </div>
          
          <div ref={cardsRef} className={s.infoCards}>
            <div className={s.infoBox}>
              <div className={s.boxHeader}>
                <span className={s.dot}></span>
                <h3>L'infrastructure</h3>
              </div>
              <p>
                Sefcheptel aide les éleveurs à surveiller leurs troupeaux en temps réel, 
                limitant les risques de pertes et garantissant un gain de temps opérationnel  grâce à l'automatisation de la détection et du comptage.
              </p>
            </div>
            <div className={s.infoBox}>
              <div className={s.boxHeader}>
                <span className={s.dot}></span>
                <h3>Notre Engagement</h3>
              </div>
              <p>
                Automatiser la détection et le comptage des animaux par espèce,
                 grâce à une analyse intelligente des flux d'imagerie terrestre et satellite.
              </p>
            </div>
          </div>

          <div ref={statsRef} className={s.statsGrid}>
            <div className={s.statBox}>
              <div className={s.numberWrapper}>
                <div className={s.statNumber} data-value="90">0</div>
                <span className={s.unit}>%</span>
              </div>
              <div className={s.statLabel}>Précision</div>
            </div>
            <div className={s.statBox}>
              <div className={s.numberWrapper}>
                <div className={s.statNumber} data-value="5000">0</div>
                <span className={s.unit}>+</span>
              </div>
              <div className={s.statLabel}>Animaux détectés</div>
            </div>
            <div className={s.statBox}>
              <div className={s.numberWrapper}>
                <div className={s.statNumber} data-value="24">0</div>
                <span className={s.unit}>/7</span>
              </div>
              <div className={s.statLabel}>Disponibilité</div>
            </div>
            <div className={s.statBox}>
              <div className={s.numberWrapper}>
                <div className={s.statNumber} data-value="300">0</div>
                <span className={s.unit}>+</span>
              </div>
              <div className={s.statLabel}>Images traitées</div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}